import { supportedLanguagesByLocaleCode } from './locales'
import { LiveLanguageCodes, ResolvedLocale, Source } from './types'
import { getLocaleCookieDomain } from './utils'

// cookie details
const STORAGE_KEY = '__monzo_locale_v1__' as const
const STORAGE_TTL_MS = 60 * 60 * 24 * 7 // 7 days

export function readLocaleCookie(cookie: string | undefined) {
  if (!cookie) {
    return null
  }

  try {
    const localeFromCookie = cookie
      .split('; ')
      .find((row) => row.startsWith(`${STORAGE_KEY}=`))
      ?.split('=')[1]

    if (!localeFromCookie) {
      return null
    }

    // Decode the URI component first
    const decodedData = decodeURIComponent(localeFromCookie)
    const parsedData = JSON.parse(decodedData)

    // Reconstruct the full locale object from minimal data
    if (
      typeof parsedData.lc !== 'string' ||
      !(parsedData.lc in supportedLanguagesByLocaleCode)
    ) {
      return null
    }

    const fullLocale =
      supportedLanguagesByLocaleCode[parsedData.lc as LiveLanguageCodes]

    const source = Object.values(Source).find((s) => s.name === parsedData.sn)
    if (!source) {
      return null
    }

    return {
      localeCode: fullLocale.localeCode,
      source: source,
      fromCookie: true,
    }
  } catch {
    // Silently ignore malformed cookies and return null
    return null
  }
}

export function createLocaleCookie(
  value: ResolvedLocale,
  maxAge: number = STORAGE_TTL_MS,
) {
  const data: CookieData = {
    lc: value.localeCode, // locale code
    sn: value.source.name, // source name
    sp: value.source.precedence, // source precedence
    ts: Date.now(), // timestamp
  }

  // Use encodeURIComponent for URL safety
  const encodedData = encodeURIComponent(JSON.stringify(data))

  const domain = getLocaleCookieDomain()
  const domainAttr = domain ? `;Domain=${domain}` : ''
  return `${STORAGE_KEY}=${encodedData};Max-Age=${maxAge};Path=/${domainAttr};Secure;SameSite=None`
}

type CookieData = {
  lc: string
  sn: string
  sp: number
  ts: number
}
